import { FormattedMessage } from 'react-intl'
import { useMediaQuery, Theme } from '@mui/material'
import { AddOutlined } from '@mui/icons-material'
import { Button } from '@carto/meridian-ds/components'
import { useAppSelector } from 'app/state'
import BasicPage from 'features/common/ui/BasicPage/BasicPage'

import MapsFilter from './MapsFilter/MapsFilter'
import MapsGallery from './MapsGallery/MapsGallery'
import useAccountHasMaps from '../state/useAccountHasMaps'
import useUserRoles from 'features/account/state/useUserRoles'
import { useAccountQuotas } from 'features/quotas/ui/hooks'
import QuotaAlert from 'features/quotas/ui/QuotaAlert'
import MapsHardQuotaModal from './MapsHardQuotaModal'
import { useHardQuotaModalController } from 'features/quotas/ui/WouldExceedQuotaDialog'
import { useMemo } from 'react'
import { QuotaUxTrigger } from 'features/hubspot/types'
import useGetAccountData from 'features/account/ui/common/useGetAccountData'
import { BuilderPaths } from 'features/builder/paths'
import { openNewWindow } from 'features/common/utils/navigationUtils'
import { useMapFilters } from '../utils/useMapFilters'

function MapsPage() {
  const { userHasEditionRole } = useUserRoles()
  const { usageBasedPricing } = useGetAccountData()

  const { accountHasMaps: hasMaps } = useAccountHasMaps()
  const maps = useAppSelector((state) => state.maps.myMaps)
  const { hasActiveFilters } = useMapFilters()
  const error = useAppSelector((state) => state.maps.myMapsError)
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.only('xs'))

  const { mapsQuota } = useAccountQuotas()
  const hardQuotaModalController = useHardQuotaModalController(mapsQuota)

  const createMap = () => {
    if (hardQuotaModalController.additionWouldExceedQuota()) {
      hardQuotaModalController.open()
      return
    }
    openNewWindow(BuilderPaths.NewEmptyMap)
  }

  const shouldDisplayNewMapButton = userHasEditionRole && hasMaps // if user has no maps, there is a special CTA

  const quotaAlert = useMemo(() => {
    return (
      !usageBasedPricing &&
      userHasEditionRole &&
      mapsQuota &&
      mapsQuota?.hit && (
        <QuotaAlert
          message={<FormattedMessage id="maps.quota.limitHit" values={{ limit: mapsQuota.limit }} />}
          moreInfoModal={(props) => (
            <MapsHardQuotaModal
              {...props}
              quota={mapsQuota}
              variant="moreInfo"
              quotaUxTrigger={QuotaUxTrigger.MoreInfo}
            />
          )}
        />
      )
    )
  }, [mapsQuota, userHasEditionRole, usageBasedPricing])

  return (
    <BasicPage
      titleId="maps.title"
      hasError={error}
      afterTitleContent={hasMaps && (maps.length > 0 || hasActiveFilters) && <MapsFilter />}
      primaryAction={
        shouldDisplayNewMapButton && (
          <Button onClick={createMap} variant="contained" color="primary" startIcon={<AddOutlined />}>
            <FormattedMessage id={`maps.${isXs ? 'titleButtonShort' : 'titleButton'}`} />
          </Button>
        )
      }
      showScrollToTop={hasMaps}
      beforeTitleContent={quotaAlert}
    >
      <MapsGallery />
      <MapsHardQuotaModal variant="create" {...hardQuotaModalController.props} quotaUxTrigger={QuotaUxTrigger.NewMap} />
    </BasicPage>
  )
}
export default MapsPage
